package ai.wagemix.logic.wages

import ai.wagemix.net.models.WMStatsCollection
import ai.wagemix.regression.addYears
import ai.wagemix.regression.processWithBackfillAndProjection
import kotlin.math.roundToInt

/** One year of the mix's weighted wage distribution. A `null` percentile had no data that year. */
data class WMWagePoint(
    val year: Int,
    val p10: Double?,
    val p25: Double?,
    val median: Double?,
    val p75: Double?,
    val p90: Double?,
    val mean: Double?,
    val projected: Boolean
)

enum class WMDataQualityIssue { Backfilled, Projected, Capped, Missing }

/** What the stats response held for one occupation of the mix, when it was less than every actual year. */
sealed interface WMOccupationDataStatus {
    /** The response has no wages for the occupation in any actual year, so it adds nothing to the mix. */
    data object NoData: WMOccupationDataStatus

    /**
     * The response skipped these actual [years]; they were estimated by a linear regression over the [basedOn]
     * years, the ones the response did hold a wage for. With a single [basedOn] year the estimate is flat at that
     * year's figures.
     */
    data class Estimated(val years: List<Int>, val basedOn: List<Int>): WMOccupationDataStatus
}

/** The headline numbers of the stat tiles for [year]. */
data class WMWageHeadline(
    val year: Int,
    val median: Double?,
    val p25: Double?,
    val p75: Double?,
    val p10: Double?,
    val p90: Double?,
    /** Median change against the previous year, as a fraction (0.031 = +3.1%). */
    val medianYearOverYear: Double?
)

/** One occupation's line in the breakdown table. */
data class WMOccupationBreakdown(
    val occupationId: Int,
    val title: String,
    /** 0..1 */
    val share: Double,
    val median: Double?
)

/** Everything the chart card, the stat tiles and the breakdown table render for one stats collection. */
data class WMWageSummary(
    val hourly: Boolean,
    val points: List<WMWagePoint>,
    val headline: WMWageHeadline,
    val breakdown: List<WMOccupationBreakdown>,
    /** The last year with real BLS data; `null` when the collection has no years at all. */
    val lastActualYear: Int?,
    /** The first year after [lastActualYear] that the summary projects, or `null` when nothing is projected. */
    val firstProjectedYear: Int?,
    val qualityIssues: Set<WMDataQualityIssue>,
    /** By occupation id, the counted occupations whose response was incomplete; a complete one has no entry. */
    val occupationStatus: Map<Int, WMOccupationDataStatus>
)

/** The most years a summary projects beyond the last actual year. */
const val WM_MAX_PROJECTED_YEARS = 10

/** Years projected beyond the last real BLS year wherever a mix is charted: the builder's default, shared and saved mixes. */
const val WM_DEFAULT_PROJECTED_YEARS = 1

/**
 * Backfills interior gaps, then projects [projectedYears] beyond the last actual BLS year (`0`
 * projects nothing), and collapses the per-occupation percentiles into one weighted distribution
 * per year. Backfilled years are estimates but not projections: only years after the last actual
 * year are `projected`.
 *
 * [shares] weights each occupation by id (0..1 of the mix); an occupation missing from it, or with
 * a share of 0, does not count and is left out of the breakdown. When [shares] is `null` the
 * collection's own weights are used, so a collection the API computed for a weighted request
 * reflects that request's weights.
 *
 * The headline is the last year up to [currentYear]; the [WMDataQualityIssue.Projected] issue is
 * raised only when that year is beyond the last actual year.
 *
 * [WMWageSummary.occupationStatus] marks each counted occupation (in [shares] with a share above 0,
 * or weighted in the collection) that the response left incomplete: [WMOccupationDataStatus.NoData]
 * when it has no wage in any actual year ([occupationsWithoutData]), and
 * [WMOccupationDataStatus.Estimated] with the actual years the backfill filled in and the years it fit
 * them from. An occupation with one year of data is held flat at that year's figures.
 *
 * An occupation without data is not part of the mix: every year's weighted figures and the
 * breakdown's shares divide among the others, in proportion to their shares, and the breakdown
 * leaves it out.
 */
fun WMStatsCollection.toWageSummary(
    hourly: Boolean,
    projectedYears: Int,
    currentYear: Int,
    shares: Map<Int, Double>? = null
): WMWageSummary {
    val actualYears = orderedStats().flatMap { (_, s) -> s.years }
    val lastActualYear = regressionMetadata?.lastActualYear ?: actualYears.maxOrNull()
    val processed = processWithBackfillAndProjection(targetYear = lastActualYear ?: currentYear)
    val extra = projectedYears.coerceIn(0, WM_MAX_PROJECTED_YEARS)
    val collection = if (extra > 0) processed.statsCollection.addYears(extra) else processed.statsCollection
    val weighted = collection.orderedStats().mapNotNull { (id, s) ->
        val weight = if (shares == null) s.weight.toDouble() else shares[id] ?: 0.0
        if (weight > 0.0) Triple(id, s, weight) else null
    }
    val years: List<Int> = weighted.flatMap { (_, s, _) -> s.years }.distinct().sorted()
    fun isProjected(year: Int): Boolean = lastActualYear != null && year > lastActualYear

    val points = years.map { year ->
        fun weightedValue(pick: (WMStatsCollection.OccupationStat) -> Double?): Double? {
            var total = 0.0
            var weight = 0.0
            weighted.forEach { (_, s, w) ->
                val value = s.statForYear(year)?.let(pick) ?: return@forEach
                total += value * w
                weight += w
            }
            return if (weight <= 0.0) null else total / weight
        }
        WMWagePoint(
            year = year,
            p10 = weightedValue { if (hourly) it.hourlyP10?.toDouble() else it.annualP10?.toDouble() },
            p25 = weightedValue { if (hourly) it.hourlyP25?.toDouble() else it.annualP25?.toDouble() },
            median = weightedValue { if (hourly) it.hourlyMedian?.toDouble() else it.annualMedian?.toDouble() },
            p75 = weightedValue { if (hourly) it.hourlyP75?.toDouble() else it.annualP75?.toDouble() },
            p90 = weightedValue { if (hourly) it.hourlyP90?.toDouble() else it.annualP90?.toDouble() },
            mean = weightedValue { if (hourly) it.hourlyMean?.toDouble() else it.annualMean?.toDouble() },
            projected = isProjected(year)
        )
    }

    val actualStats = weighted.flatMap { (_, s, _) -> s.years.filterNot(::isProjected).mapNotNull(s::statForYear) }
    val issues = buildSet {
        if (processed.hasBackfilledData) {
            add(WMDataQualityIssue.Backfilled)
        }
        if (actualStats.any { it.cappedFlags != 0 }) {
            add(WMDataQualityIssue.Capped)
        }
        if (actualStats.any { it.missingFlags != 0 }) {
            add(WMDataQualityIssue.Missing)
        }
    }

    val received = orderedStats().toMap()
    val filledIn = processed.statsCollection.orderedStats().toMap()
    val counted = shares?.filterValues { it > 0.0 }?.keys ?: weighted.map { (id, _, _) -> id }.toSet()
    val withoutData = occupationsWithoutData(counted, hourly)
    val occupationStatus = counted.mapNotNull { id ->
        if (id in withoutData) {
            return@mapNotNull id to WMOccupationDataStatus.NoData
        }
        val original = received[id]
        val originalYears = original?.years.orEmpty().toSet()
        val estimatedYears = filledIn[id]?.let { s ->
            s.years
                .filter { year -> year !in originalYears && !isProjected(year) && s.statForYear(year).hasWage(hourly) }
                .sorted()
        }.orEmpty()
        if (estimatedYears.isEmpty()) {
            return@mapNotNull null
        }
        val basedOn = originalYears.filter { year -> !isProjected(year) && original?.statForYear(year).hasWage(hourly) }
        id to WMOccupationDataStatus.Estimated(years = estimatedYears, basedOn = basedOn.sorted())
    }.toMap()

    val now = points.lastOrNull { it.year <= currentYear } ?: points.lastOrNull()
    val previous = now?.let { n -> points.lastOrNull { it.year == n.year - 1 } }
    val headline = WMWageHeadline(
        year = now?.year ?: currentYear,
        median = now?.median,
        p25 = now?.p25,
        p75 = now?.p75,
        p10 = now?.p10,
        p90 = now?.p90,
        medianYearOverYear = now?.median?.let { m -> previous?.median?.takeIf { it > 0 }?.let { p -> (m - p) / p } }
    )

    // An occupation without data is not part of the mix: the others share its weight.
    val contributing = weighted.filter { (id, _, _) -> id !in withoutData }
    val totalWeight = contributing.sumOf { (_, _, w) -> w }
    val headlineYear = headline.year
    val breakdown = contributing.map { (id, s, w) ->
        val stat = s.statForYear(headlineYear) ?: s.years.filter { it <= headlineYear }.maxOrNull()?.let { s.statForYear(it) }
        WMOccupationBreakdown(
            occupationId = id,
            title = s.title,
            share = w / totalWeight,
            median = if (hourly) stat?.hourlyMedian?.toDouble() else stat?.annualMedian?.toDouble()
        )
    }.sortedByDescending { it.share }

    return WMWageSummary(
        hourly = hourly,
        points = points,
        headline = headline,
        breakdown = breakdown,
        lastActualYear = lastActualYear,
        firstProjectedYear = points.firstOrNull { it.projected }?.year,
        qualityIssues = if (now != null && now.projected) issues + WMDataQualityIssue.Projected else issues,
        occupationStatus = occupationStatus
    )
}

/**
 * The occupations of [ids] this collection has no wage for, in the hourly or the annual figures, in any actual year;
 * an occupation the collection leaves out has none. They are not part of the mix.
 */
fun WMStatsCollection.occupationsWithoutData(ids: Collection<Int>, hourly: Boolean): Set<Int> {
    val lastActualYear = regressionMetadata?.lastActualYear
    val received = orderedStats().toMap()
    return ids.filterTo(mutableSetOf()) { id ->
        val stats = received[id]
        stats == null || stats.years.none { year ->
            (lastActualYear == null || year <= lastActualYear) && stats.statForYear(year).hasWage(hourly)
        }
    }
}

// A year holds a wage when any percentile of the chosen figures is present.
private fun WMStatsCollection.OccupationStat?.hasWage(hourly: Boolean): Boolean {
    if (this == null) {
        return false
    }
    val figures: List<Number?> = if (hourly) {
        listOf(hourlyP10, hourlyP25, hourlyMedian, hourlyP75, hourlyP90)
    } else {
        listOf(annualP10, annualP25, annualMedian, annualP75, annualP90)
    }
    return figures.any { it != null }
}

/** Rounds a wage for display decisions (the UI formats; this keeps comparisons stable). */
fun Double.roundedWage(hourly: Boolean): Double = if (hourly) (this * 100).roundToInt() / 100.0 else roundToInt().toDouble()
